import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Trash2, TrendingUp, TrendingDown, Loader2, Download, CreditCard,
  ArrowDownWideNarrow, ArrowUpNarrowWide, ListFilter, Info, X,
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, getLocaleFromLang, pickLocalizedText } from '../../utils/localizedContent';

const EXPENSE_CATEGORIES = [
  { key: 'stock',      label_es: 'Compra de stock',     label_en: 'Stock purchase' },
  { key: 'prize',      label_es: 'Premio de torneo',    label_en: 'Tournament prize' },
  { key: 'tax',        label_es: 'Impuestos',           label_en: 'Taxes' },
  { key: 'rent',       label_es: 'Alquiler/Suministros',label_en: 'Rent/Utilities' },
  { key: 'marketing',  label_es: 'Marketing',           label_en: 'Marketing' },
  { key: 'other',      label_es: 'Otros gastos',        label_en: 'Other expenses' },
];

const INCOME_CATEGORIES = [
  { key: 'sale',       label_es: 'Venta tienda',        label_en: 'Store sale' },
  { key: 'entry_fee',  label_es: 'Cuota de torneo',     label_en: 'Tournament entry' },
  { key: 'rental',     label_es: 'Alquiler de juegos',  label_en: 'Game rental' },
  { key: 'other',      label_es: 'Otros ingresos',      label_en: 'Other income' },
];

function BarRow({ label, value, max, barClass }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs gap-2">
        <span className="text-[#A1A1AA] truncate">{label}</span>
        <span className="text-[#F5F0E6] font-semibold tabular-nums flex-shrink-0">{value.toFixed(2)}€</span>
      </div>
      <div className="h-2 bg-black/30 rounded-full overflow-hidden border border-white/5">
        <div className={`h-full rounded-full transition-all ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdminBilling({ lang, setMessage, onNavigateToOrder }) {
  const [data, setData] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const [manualSessionId, setManualSessionId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [activeIssueType, setActiveIssueType] = useState('pending_transaction_no_order');
  const [lastRevalidatedAt, setLastRevalidatedAt] = useState('');
  const [form, setForm] = useState({ type: 'expense', category: 'stock', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [filterType, setFilterType] = useState('all');
  const [sortMode, setSortMode] = useState('date_desc');

  const l = createTextPicker(lang);

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    try {
      const cacheBust = Date.now();
      const [billingResp, reconciliationResp] = await Promise.all([
        axios.get(`${API}/admin/billing`, { params: { period } }),
        axios.get(`${API}/admin/billing/reconciliation`, {
          params: { period, verify_with_stripe: true, _t: cacheBust },
          headers: { 'Cache-Control': 'no-cache' },
        }),
      ]);
      const d = billingResp.data;
      setData(d);
      setReconciliation(reconciliationResp.data);
      setLastRevalidatedAt(new Date().toISOString());
    } catch (err) {
      setMessage(formatApiError(err));
    }
    setLoading(false);
  }, [period]);

  const fetchReconciliation = useCallback(async (verifyWithStripe = false) => {
    setReconciliationLoading(true);
    try {
      const cacheBust = Date.now();
      const { data: r } = await axios.get(`${API}/admin/billing/reconciliation`, {
        params: { period, verify_with_stripe: verifyWithStripe, _t: cacheBust },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setReconciliation(r);
      setLastRevalidatedAt(new Date().toISOString());
      setMessage(
        verifyWithStripe
          ? l('Conciliación live actualizada desde Stripe', 'Live reconciliation updated from Stripe')
          : l('Conciliación actualizada', 'Reconciliation updated'),
      );
    } catch (err) {
      setMessage(formatApiError(err));
    } finally {
      setReconciliationLoading(false);
    }
  }, [period, setMessage, l]);

  const repairCheckoutSessions = useCallback(
    async (sessionIds) => {
      const ids = [...new Set(sessionIds)].filter(Boolean).slice(0, 25);
      if (!ids.length) return null;
      if (!API) {
        setMessage(
          l(
            'Falta REACT_APP_BACKEND_URL; no se puede llamar al API.',
            'REACT_APP_BACKEND_URL is missing; cannot call the API.',
          ),
        );
        return null;
      }
      setRepairLoading(true);
      try {
        const { data } = await axios.post(`${API}/admin/billing/fulfill-checkout-session`, {
          session_ids: ids,
        });
        const rebuiltCount = (data.results || []).filter((r) => r.rebuilt_transaction).length;
        const createdOrExistingCount = (data.results || []).filter((r) => r.ok).length;
        const failed = (data.results || []).filter((r) => !r.ok);
        if (failed.length) {
          const reasons = failed
            .map((f) => {
              const label = f.requested_id && f.requested_id !== f.session_id
                ? `${f.requested_id}→${f.session_id}`
                : (f.session_id || f.requested_id);
              const details = [
                f.reason,
                f.stripe_code,
                f.stripe_error_type,
                f.stripe_param ? `param=${f.stripe_param}` : null,
                f.stripe_user_message || f.stripe_error_message,
              ].filter(Boolean).join(' | ');
              return `${label}: ${details || 'error'}`;
            })
            .join('; ');
          setMessage(
            l(`Reparación parcial (${failed.length}): ${reasons}`, `Partial repair (${failed.length}): ${reasons}`),
          );
        } else {
          setMessage(
            l(
              `Reparación aplicada (${createdOrExistingCount}). Transacciones reconstruidas: ${rebuiltCount}.`,
              `Repair applied (${createdOrExistingCount}). Rebuilt transactions: ${rebuiltCount}.`,
            ),
          );
        }
        await fetchReconciliation(true);
        return data;
      } catch (err) {
        setMessage(formatApiError(err));
        return null;
      } finally {
        setRepairLoading(false);
      }
    },
    [fetchReconciliation, setMessage, l],
  );

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setMessage(l('El importe debe ser mayor que 0', 'Amount must be greater than 0'));
      return;
    }
    try {
      await axios.post(`${API}/admin/billing`, { ...form, amount: parseFloat(form.amount) });
      setMessage(l('Entrada añadida', 'Entry added'));
      setShowForm(false);
      setForm({ type: 'expense', category: 'stock', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      fetchBilling();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm(l('¿Eliminar esta entrada?', 'Delete this entry?'))) return;
    try {
      await axios.delete(`${API}/admin/billing/${entryId}`);
      setMessage(l('Eliminado', 'Deleted'));
      fetchBilling();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const getCatLabel = (type, cat) => {
    const list = type === 'expense' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;
    const category = list.find((c) => c.key === cat);
    return category ? pickLocalizedText(lang, category.label_es, category.label_en) : cat;
  };

  const categories = form.type === 'expense' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;

  const entries = data?.entries || [];
  const incomeByCategory = useMemo(() => {
    return Object.entries(
      entries.filter((e) => e.type === 'income').reduce((acc, e) => {
        const lab = getCatLabel('income', e.category);
        acc[lab] = (acc[lab] || 0) + e.amount;
        return acc;
      }, {}),
    );
  }, [entries, lang]);

  const expenseByCategory = useMemo(() => {
    return Object.entries(
      entries.filter((e) => e.type === 'expense').reduce((acc, e) => {
        const lab = getCatLabel('expense', e.category);
        acc[lab] = (acc[lab] || 0) + e.amount;
        return acc;
      }, {}),
    );
  }, [entries, lang]);

  const maxExpenseCat = useMemo(() => {
    if (!expenseByCategory.length) return 1;
    return Math.max(...expenseByCategory.map(([, v]) => v), 1);
  }, [expenseByCategory]);

  const filteredSortedEntries = useMemo(() => {
    let list = [...entries];
    if (filterType === 'income') list = list.filter((e) => e.type === 'income');
    if (filterType === 'expense') list = list.filter((e) => e.type === 'expense');
    list.sort((a, b) => {
      const da = (a.date || '').split('T')[0];
      const db = (b.date || '').split('T')[0];
      if (sortMode === 'date_asc') return da.localeCompare(db);
      if (sortMode === 'date_desc') return db.localeCompare(da);
      if (sortMode === 'amount_desc') return (b.amount || 0) - (a.amount || 0);
      if (sortMode === 'amount_asc') return (a.amount || 0) - (b.amount || 0);
      return 0;
    });
    return list;
  }, [entries, filterType, sortMode]);

  const periodLabel = () => {
    if (period === 'month') return l('Este mes', 'This month');
    if (period === 'year') return l('Este año', 'This year');
    return l('Todo el historial', 'All time');
  };

  const formatWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(getLocaleFromLang(lang), {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const exportCsv = () => {
    if (!data) return;
    const sep = ';';
    const lines = [
      `\ufeff${l('Facturación', 'Billing')} — ${periodLabel()}`,
      `${l('Ingresos pedidos (web)', 'Order income (web)')}${sep}${(data.order_income ?? 0).toFixed(2)}`,
      `${l('Ingresos manuales', 'Manual income')}${sep}${(data.manual_income ?? 0).toFixed(2)}`,
      `${l('Gastos', 'Expenses')}${sep}${(data.total_expenses ?? 0).toFixed(2)}`,
      `${l('Neto', 'Net')}${sep}${(data.net ?? 0).toFixed(2)}`,
      '',
      `${l('Fecha', 'Date')}${sep}${l('Tipo', 'Type')}${sep}${l('Categoría', 'Category')}${sep}${l('Descripción', 'Description')}${sep}${l('Importe', 'Amount')}`,
    ];
    entries.forEach((e) => {
      const row = [
        (e.date || '').split('T')[0],
        e.type === 'income' ? l('Ingreso', 'Income') : l('Gasto', 'Expense'),
        getCatLabel(e.type, e.category),
        (e.description || '').replace(/"/g, '""'),
        String(e.amount ?? 0).replace('.', ','),
      ];
      lines.push(row.map((c) => `"${c}"`).join(sep));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setMessage(l('CSV descargado', 'CSV downloaded'));
  };

  const manualIncome = data?.manual_income ?? 0;
  const orderIncome = data?.order_income ?? 0;
  const incomeSplitSum = manualIncome + orderIncome || 1;
  const reconciliationIssues = reconciliation?.issue_counts || {};
  const stripePaidDrift = reconciliation?.issues?.stripe_paid_local_pending ?? [];
  const topIssues = [
    {
      key: 'pending_transaction_no_order',
      label: l('Pendiente local sin pedido', 'Local pending, no order'),
      repairMode: 'conditional',
    },
    {
      key: 'stripe_paid_local_pending',
      label: l('Stripe cobró · local pendiente', 'Stripe paid · local pending'),
      repairMode: 'direct',
    },
    {
      key: 'order_missing_transaction',
      label: l('Pedidos sin transacción', 'Orders without transaction'),
      repairMode: 'direct',
    },
    {
      key: 'transaction_order_status_drift',
      label: l('Transacción vs estado del pedido', 'Txn vs order status'),
      repairMode: 'info',
    },
    {
      key: 'transaction_missing_order',
      label: l('Transacciones sin pedido', 'Transactions without order'),
      repairMode: 'direct',
    },
    {
      key: 'missing_payment_intent',
      label: l('Sin payment intent', 'Missing payment intent'),
      repairMode: 'info',
    },
    {
      key: 'refunded_missing_refund_id',
      label: l('Reembolsos sin ID', 'Refunds without ID'),
      repairMode: 'info',
    },
    {
      key: 'stripe_status_mismatch',
      label: l('Discrepancia live Stripe', 'Live Stripe mismatch'),
      repairMode: 'info',
    },
    {
      key: 'stripe_live_check_failed',
      label: l('Fallo verificación live Stripe', 'Live Stripe check failed'),
      repairMode: 'info',
    },
  ];
  const activeIssueItems = reconciliation?.issues?.[activeIssueType] || [];
  const activeIssueConfig = topIssues.find((issue) => issue.key === activeIssueType);
  const activeIssueRepairMode = activeIssueConfig?.repairMode || 'info';
  const getIssueCause = useCallback((issueType, item) => {
    if (issueType === 'pending_transaction_no_order') {
      return l(
        'La transacción local sigue pendiente/iniciada y no existe pedido asociado.',
        'Local transaction is still pending/initiated and no linked order exists.',
      );
    }
    if (issueType === 'stripe_paid_local_pending') {
      return l(
        'Stripe ya marca la sesión como pagada, pero en local aún figura pendiente/sin pedido.',
        'Stripe already marks the session as paid, but local data is still pending/no order.',
      );
    }
    if (issueType === 'order_missing_transaction') {
      return l(
        'Existe pedido local, pero falta su payment_transaction asociado.',
        'Local order exists, but its linked payment_transaction is missing.',
      );
    }
    if (issueType === 'transaction_order_status_drift') {
      return l(
        'Hay pedido enlazado a la sesión, pero el estado del pedido no coincide con la transacción (p. ej. pedido en pendiente y transacción pagada). Corrige el estado del pedido o revisa si hubo un cambio manual erróneo.',
        'An order exists for this checkout session, but its status does not match the payment transaction (e.g. order pending while the transaction is paid). Fix the order status or review accidental manual edits.',
      );
    }
    if (issueType === 'transaction_missing_order') {
      return l(
        'Hay transacción pagada/reembolsada en BD pero no existe ningún pedido con esa sesión de checkout.',
        'A paid/refunded transaction exists locally, but no order row exists for that checkout session.',
      );
    }
    if (issueType === 'missing_payment_intent') {
      return l(
        'Falta el Stripe PaymentIntent en pedido/transacción para esa sesión.',
        'Stripe PaymentIntent is missing in order/transaction for that session.',
      );
    }
    if (issueType === 'refunded_missing_refund_id') {
      return l(
        'El pedido está en estado refunded, pero falta el identificador de reembolso.',
        'Order is marked refunded, but refund identifier is missing.',
      );
    }
    if (issueType === 'stripe_status_mismatch') {
      return l(
        `Estado local y estado live de Stripe no coinciden (local=${item?.local_status || 'n/a'}, stripe=${item?.stripe_status || 'n/a'}).`,
        `Local and live Stripe status do not match (local=${item?.local_status || 'n/a'}, stripe=${item?.stripe_status || 'n/a'}).`,
      );
    }
    if (issueType === 'stripe_live_check_failed') {
      const errorType = item?.stripe_error_type || 'StripeError';
      const errorCode = item?.stripe_error_code ? ` (${item?.stripe_error_code})` : '';
      const fallbackMsg = l(
        'No se pudo consultar Stripe en vivo para validar el PaymentIntent.',
        'Could not reach live Stripe to validate the PaymentIntent.',
      );
      const detail = (item?.stripe_error_message || '').trim() || fallbackMsg;
      return l(
        `Falló la consulta en vivo a Stripe: ${errorType}${errorCode}. ${detail}`,
        `Live Stripe check failed: ${errorType}${errorCode}. ${detail}`,
      );
    }
    return l('Causa no clasificada.', 'Unclassified cause.');
  }, [l]);

  const copyText = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setMessage(l('Copiado al portapapeles', 'Copied to clipboard'));
    } catch (_err) {
      setMessage(l('No se pudo copiar', 'Could not copy'));
    }
  };

  const inputClass = 'w-full bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#C9A962]';
  const openNativeDatePicker = (event) => {
    const input = event.currentTarget;
    if (typeof input?.showPicker === 'function') input.showPicker();
  };

  return (
    <div className="space-y-6" data-testid="admin-billing-root">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2
            className="text-[#F5F0E6] font-bold text-2xl tracking-tight"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            {l('Facturación', 'Billing')}
          </h2>
          <p className="text-[#A1A1AA] text-sm mt-1 flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#C9A962]/15 text-[#C9A962] text-xs font-semibold border border-[#C9A962]/25">
              {periodLabel()}
            </span>
            {l('Resumen interno + ventas web (Stripe)', 'Internal P&L + web sales (Stripe)')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowInfo(true)}
            className="px-3 py-2 text-sm rounded-xl border border-[#C9A962]/25 text-[#C9A962] hover:bg-[#C9A962]/10 flex items-center gap-2"
            title={l('Cómo funciona este panel', 'How this panel works')}
          >
            <Info size={15} />
            {l('Info', 'Info')}
          </button>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-[#12121A] border border-[#C9A962]/20 text-[#F5F0E6] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A962]"
          >
            <option value="month">{l('Este mes', 'This month')}</option>
            <option value="year">{l('Este año', 'This year')}</option>
            <option value="all">{l('Todo', 'All time')}</option>
          </select>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!data || loading}
            className="px-4 py-2 text-sm rounded-xl border border-white/15 text-[#A1A1AA] hover:text-[#F5F0E6] hover:border-[#C9A962]/40 flex items-center gap-2 disabled:opacity-40"
          >
            <Download size={15} />
            CSV
          </button>
          <button
            type="button"
            onClick={() => setShowForm((s) => !s)}
            className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
          >
            <Plus size={15} />
            {l('Añadir entrada', 'Add entry')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-[#C9A962]" />
        </div>
      ) : !data ? null : (
        <>
          {/* Hero net + KPI strip */}
          <div className="rounded-2xl border border-[#C9A962]/25 bg-gradient-to-br from-[#0F1F1A] via-[#0A1612] to-[#0F1F1A] p-6 sm:p-8 shadow-lg shadow-black/20">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <p className="text-[#A8B5AE] text-sm font-medium uppercase tracking-wider">
                  {l('Resultado neto del periodo', 'Net result for period')}
                </p>
                <p
                  className={`text-4xl sm:text-5xl font-black tabular-nums mt-1 ${
                    (data.net ?? 0) >= 0 ? 'text-[#C9A962]' : 'text-[#F87171]'
                  }`}
                >
                  {(data.net ?? 0) >= 0 ? '+' : ''}
                  {data.net?.toFixed(2)}€
                </p>
                <p className="text-[#52525B] text-xs mt-2 max-w-md">
                  {l(
                    'Incluye ventas online confirmadas y tus apuntes manuales. No sustituye al Dashboard de Stripe.',
                    'Includes confirmed online sales and your manual entries. Does not replace the Stripe Dashboard.',
                  )}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full lg:w-auto lg:min-w-[420px]">
                <div className="rounded-xl bg-[#10B981]/10 border border-[#10B981]/25 px-3 py-3 text-center">
                  <p className="text-[#52525B] text-[10px] uppercase font-semibold">{l('Ingresos', 'Income')}</p>
                  <p className="text-[#34D399] font-bold text-lg tabular-nums">{data.total_income?.toFixed(2)}€</p>
                </div>
                <div className="rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/25 px-3 py-3 text-center">
                  <p className="text-[#52525B] text-[10px] uppercase font-semibold">{l('Gastos', 'Expenses')}</p>
                  <p className="text-[#F87171] font-bold text-lg tabular-nums">{data.total_expenses?.toFixed(2)}€</p>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-center">
                  <p className="text-[#52525B] text-[10px] uppercase font-semibold">{l('Pedidos', 'Orders')}</p>
                  <p className="text-[#F5F0E6] font-bold text-lg tabular-nums">{data.order_count ?? 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Two columns: online sales vs breakdown */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-[#10B981]/20 bg-[#0F1F1A]/80 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                  <CreditCard size={18} className="text-[#34D399]" />
                </div>
                <div>
                  <h3 className="text-[#F5F0E6] font-bold text-sm">{l('Ventas online (Checkout)', 'Online sales (Checkout)')}</h3>
                  <p className="text-[#52525B] text-xs">
                    {data.order_count ?? 0}{' '}
                    {l('pedidos · total', 'orders · total')}{' '}
                    <span className="text-[#34D399] font-semibold">{orderIncome.toFixed(2)}€</span>
                  </p>
                </div>
              </div>
              {(data.recent_orders || []).length === 0 ? (
                <p className="text-[#52525B] text-sm py-6 text-center border border-dashed border-white/10 rounded-xl">
                  {l('Sin pedidos pagados en este periodo.', 'No paid orders in this period.')}
                </p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/5">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[#52525B] text-xs uppercase border-b border-white/10">
                        <th className="px-3 py-2 font-semibold">{l('Pedido', 'Order')}</th>
                        <th className="px-3 py-2 font-semibold">{l('Fecha', 'When')}</th>
                        <th className="px-3 py-2 font-semibold text-right">{l('Total', 'Total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recent_orders || []).map((o) => (
                        <tr key={o.order_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                          <td className="px-3 py-2.5 font-mono text-xs text-[#C9A962]">{o.order_alias || o.order_id}</td>
                          <td className="px-3 py-2.5 text-[#A1A1AA] text-xs whitespace-nowrap">{formatWhen(o.created_at)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-[#F5F0E6] tabular-nums">{o.total?.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-[#C9A962]/20 bg-[#0F1F1A]/80 p-5 space-y-5">
              <h3 className="text-[#F5F0E6] font-bold text-sm">{l('Composición de ingresos', 'Income composition')}</h3>
              <div className="space-y-2">
                <div className="flex h-3 rounded-full overflow-hidden border border-white/10">
                  <div
                    className="bg-[#34D399]"
                    style={{ width: `${(orderIncome / incomeSplitSum) * 100}%` }}
                    title={l('Web', 'Web')}
                  />
                  <div
                    className="bg-[#059669]"
                    style={{ width: `${(manualIncome / incomeSplitSum) * 100}%` }}
                    title={l('Manual', 'Manual')}
                  />
                </div>
                <div className="flex flex-wrap gap-4 text-xs">
                  <span className="flex items-center gap-1.5 text-[#A1A1AA]">
                    <span className="w-2 h-2 rounded-full bg-[#34D399]" />
                    {l('Ventas web', 'Web sales')} · {orderIncome.toFixed(2)}€
                  </span>
                  <span className="flex items-center gap-1.5 text-[#A1A1AA]">
                    <span className="w-2 h-2 rounded-full bg-[#059669]" />
                    {l('Ingresos manuales', 'Manual income')} · {manualIncome.toFixed(2)}€
                  </span>
                </div>
              </div>
              <div>
                <h4 className="text-[#52525B] text-xs font-semibold uppercase tracking-wider mb-3">
                  {l('Gastos por categoría', 'Expenses by category')}
                </h4>
                <div className="space-y-3">
                  {expenseByCategory.length === 0 ? (
                    <p className="text-[#52525B] text-xs">{l('Sin gastos en el periodo.', 'No expenses in period.')}</p>
                  ) : (
                    expenseByCategory.map(([cat, total]) => (
                      <BarRow key={cat} label={cat} value={total} max={maxExpenseCat} barClass="bg-gradient-to-r from-[#F87171] to-[#EF4444]" />
                    ))
                  )}
                </div>
              </div>
              {incomeByCategory.length > 0 && (
                <div>
                  <h4 className="text-[#52525B] text-xs font-semibold uppercase tracking-wider mb-3">
                    {l('Ingresos manuales por categoría', 'Manual income by category')}
                  </h4>
                  <div className="space-y-3">
                    {incomeByCategory.map(([cat, total]) => (
                      <BarRow
                        key={cat}
                        label={cat}
                        value={total}
                        max={Math.max(...incomeByCategory.map(([, v]) => v), 1)}
                        barClass="bg-gradient-to-r from-[#6EE7B7] to-[#34D399]"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[#C9A962]/20 bg-[#0F1F1A]/80 p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-[#F5F0E6] font-bold text-sm">{l('Conciliación Stripe', 'Stripe reconciliation')}</h3>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs px-2 py-1 rounded-md border ${
                    reconciliation?.healthy
                      ? 'text-[#34D399] border-[#34D399]/35 bg-[#34D399]/10'
                      : 'text-[#F87171] border-[#F87171]/35 bg-[#F87171]/10'
                  }`}
                >
                  {reconciliation?.healthy ? l('Sin incidencias', 'No issues') : l('Con incidencias', 'Issues detected')}
                </span>
                <button
                  type="button"
                  onClick={() => fetchReconciliation(true)}
                  disabled={reconciliationLoading || repairLoading}
                  className="px-3 py-1.5 rounded-lg border border-[#C9A962]/30 text-[#C9A962] hover:bg-[#C9A962]/10 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reconciliationLoading ? l('Validando...', 'Checking...') : l('Revalidar live', 'Live recheck')}
                </button>
                {stripePaidDrift.length > 0 && (
                  <button
                    type="button"
                    onClick={() => repairCheckoutSessions(stripePaidDrift.map((row) => row.session_id))}
                    disabled={repairLoading || reconciliationLoading}
                    className="px-3 py-1.5 rounded-lg border border-[#34D399]/35 text-[#34D399] hover:bg-[#34D399]/10 text-xs disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {repairLoading ? <Loader2 className="animate-spin" size={14} aria-hidden /> : null}
                    {l('Reparar desfases', 'Repair drift')} ({Math.min(stripePaidDrift.length, 25)})
                  </button>
                )}
              </div>
            </div>
            <p className="text-[#A1A1AA] text-xs">
              {l(
                'Incluye transacciones en estado pendiente/iniciado sin pedido (antes solo se escaneaban pagos confirmados en BD). «Revalidar live» compara esas sesiones con Stripe.',
                'Includes pending/initiated transactions with no order (previously only DB-confirmed payments were scanned). “Live recheck” compares those sessions to Stripe.',
              )}
            </p>
            {lastRevalidatedAt && (
              <p className="text-[#52525B] text-[11px]">
                {l('Última revalidación', 'Last revalidation')}: {new Date(lastRevalidatedAt).toLocaleString(getLocaleFromLang(lang))}
              </p>
            )}
            {!API && (
              <p className="text-[#F87171] text-xs font-medium">
                {l(
                  'Sin REACT_APP_BACKEND_URL el panel no puede llamar al API (reparación y facturación fallarán).',
                  'Without REACT_APP_BACKEND_URL the panel cannot reach the API (repair and billing will fail).',
                )}
              </p>
            )}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 items-stretch sm:items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-[10px] uppercase text-[#52525B] block mb-1">
                  {l('Reparar por ID (sesión Stripe o txn interno)', 'Repair by ID (Stripe session or internal txn)')}
                </label>
                <input
                  type="text"
                  value={manualSessionId}
                  onChange={(e) => setManualSessionId(e.target.value.trim())}
                  placeholder="cs_test_… / cs_live_… o txn_… (BD)"
                  className="w-full bg-[#0A0A0E] border border-white/10 rounded-xl px-3 py-2 text-sm text-[#F5F0E6] placeholder:text-[#52525B] outline-none focus:border-[#34D399]/50"
                  disabled={repairLoading}
                />
                <p className="text-[#52525B] text-[10px] mt-1 leading-snug">
                  {l(
                    'Preferido: cs_… (Checkout). También puedes pegar el txn_… de nuestra base (no el txn_… largo de balance de Stripe).',
                    'Prefer cs_… (Checkout). You can also paste our DB txn_… (not Stripe’s long balance txn_…).',
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  const id = manualSessionId.trim();
                  if (!id) {
                    setMessage(
                      l(
                        'Pega un cs_… (sesión Stripe) o el txn_… interno de la tienda.',
                        'Paste a cs_… (Stripe session) or the store’s internal txn_… id.',
                      ),
                    );
                    return;
                  }
                  const res = await repairCheckoutSessions([id]);
                  if (res?.ok) setManualSessionId('');
                }}
                disabled={repairLoading || reconciliationLoading}
                className="px-3 py-2 rounded-xl border border-[#34D399]/35 text-[#34D399] hover:bg-[#34D399]/10 text-xs disabled:opacity-50 shrink-0"
              >
                {repairLoading ? l('Aplicando…', 'Applying…') : l('Reparar esta sesión', 'Repair this session')}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {topIssues.map((issue) => (
                <button
                  key={issue.key}
                  type="button"
                  onClick={() => setActiveIssueType(issue.key)}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    activeIssueType === issue.key
                      ? 'border-[#FCA5A5] bg-[#F87171]/15'
                      : 'border-white/10 bg-black/20 hover:border-[#C9A962]/30'
                  }`}
                >
                  <p className="text-[#52525B] text-[10px] uppercase">{issue.label}</p>
                  <p className="text-[#F5F0E6] font-bold text-lg tabular-nums">{reconciliationIssues[issue.key] || 0}</p>
                </button>
              ))}
            </div>
            <div className="text-xs text-[#52525B]">
              {l('Escaneados', 'Scanned')}: {reconciliation?.orders_scanned || 0} {l('pedidos', 'orders')} ·{' '}
              {reconciliation?.transactions_scanned || 0} {l('transacciones pagadas/refund', 'paid/refund txns')} ·{' '}
              {reconciliation?.pending_transactions_scanned ?? '—'} {l('pendientes', 'pending')}
            </div>
            {reconciliation && !reconciliation.healthy && (
              <div className="rounded-xl border border-[#F87171]/25 bg-[#F87171]/5 p-3">
                <p className="text-[#FCA5A5] text-xs font-semibold mb-2">
                  {l('Incidencias detectadas', 'Detected issues')}
                </p>
                <p className="text-[#A1A1AA] text-[11px] mb-2">
                  {activeIssueRepairMode === 'info'
                    ? l('Tipo informativo: revisar manualmente en Stripe/BD.', 'Informational type: review manually in Stripe/DB.')
                    : activeIssueRepairMode === 'conditional'
                      ? l('Reparable solo cuando Stripe confirma pago para la sesión.', 'Repairable only when Stripe confirms payment for the session.')
                      : l('Reparable desde este panel.', 'Repairable from this panel.')}
                </p>
                {activeIssueItems.length === 0 ? (
                  <p className="text-[#A1A1AA] text-xs">{l('No hay elementos para este tipo.', 'No items for this type.')}</p>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-auto pr-1">
                    {activeIssueItems.slice(0, 20).map((item, idx) => {
                      const primaryId =
                        item.session_id ||
                        item.order_id ||
                        item.transaction_id ||
                        item.stripe_payment_intent_id ||
                        `item_${idx}`;
                      const secondaryId = [item.transaction_id, item.stripe_payment_intent_id, item.order_id]
                        .filter(Boolean)
                        .filter((id) => id !== primaryId)
                        .join(' · ');
                      const statusLine =
                        activeIssueType === 'transaction_order_status_drift' &&
                        (item.order_status != null || item.payment_status != null)
                          ? `${l('Pedido', 'Order')}: ${item.order_status ?? '—'} · ${l('Transacción', 'Txn')}: ${item.payment_status ?? '—'}`
                          : '';
                      return (
                        <div key={`${activeIssueType}-${primaryId}-${idx}`} className="rounded-lg border border-white/10 bg-black/20 p-2">
                          <p className="text-[#F5F0E6] text-xs font-medium">{primaryId}</p>
                          {secondaryId && <p className="text-[#A1A1AA] text-[11px] mt-0.5">{secondaryId}</p>}
                          {statusLine && <p className="text-[#FBBF24]/90 text-[11px] mt-0.5 font-medium">{statusLine}</p>}
                          <p className="text-[#A1A1AA] text-[11px] mt-1">
                            {getIssueCause(activeIssueType, item)}
                          </p>
                          <div className="mt-1">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => copyText(primaryId)}
                                className="text-[11px] px-2 py-1 rounded-md border border-white/10 text-[#C9A962] hover:bg-[#C9A962]/10"
                              >
                                {l('Copiar ID', 'Copy ID')}
                              </button>
                              {item.order_id && typeof onNavigateToOrder === 'function' && (
                                <button
                                  type="button"
                                  onClick={() => onNavigateToOrder(item.order_id)}
                                  className="text-[11px] px-2 py-1 rounded-md border border-[#C9A962]/25 text-[#F5F0E6] hover:bg-[#C9A962]/10"
                                >
                                  {l('Ir al pedido', 'Go to order')}
                                </button>
                              )}
                              {activeIssueRepairMode !== 'info' && item.session_id && (
                                <button
                                  type="button"
                                  onClick={() => repairCheckoutSessions([item.session_id])}
                                  disabled={repairLoading || reconciliationLoading}
                                  className="text-[11px] px-2 py-1 rounded-md border border-[#34D399]/35 text-[#34D399] hover:bg-[#34D399]/10 disabled:opacity-50"
                                >
                                  {l('Reparar', 'Repair')}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Add form */}
          {showForm && (
            <div className="bg-[#12121A] border border-[#C9A962]/20 rounded-2xl p-5">
              <h3 className="text-[#F5F0E6] font-bold mb-4" style={{ fontFamily: 'Cinzel, serif' }}>
                {l('Nueva entrada', 'New entry')}
              </h3>
              <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[#A1A1AA] text-xs font-medium mb-1 block">{l('Tipo', 'Type')}</label>
                  <select
                    value={form.type}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        type: e.target.value,
                        category: e.target.value === 'expense' ? 'stock' : 'sale',
                      }))
                    }
                    className={inputClass}
                  >
                    <option value="expense">{l('Gasto', 'Expense')}</option>
                    <option value="income">{l('Ingreso', 'Income')}</option>
                  </select>
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-xs font-medium mb-1 block">{l('Categoría', 'Category')}</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className={inputClass}
                  >
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>
                        {pickLocalizedText(lang, c.label_es, c.label_en)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-xs font-medium mb-1 block">{l('Descripción', 'Description')}</label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder={l('Ej: Compra de 50 sobres', 'e.g. 50 booster packs')}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-xs font-medium mb-1 block">
                    {l('Importe (€)', 'Amount (€)')} *
                  </label>
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-xs font-medium mb-1 block">{l('Fecha', 'Date')}</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    onClick={openNativeDatePicker}
                    onFocus={openNativeDatePicker}
                    className={inputClass}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button type="submit" className="flex-1 btn-primary py-2.5 text-sm">
                    {l('Guardar', 'Save')}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 bg-white/5 text-[#A1A1AA] rounded-xl text-sm">
                    {l('Cancelar', 'Cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Manual ledger */}
          <div className="rounded-2xl border border-white/10 bg-[#12121A]/60 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-white/10 bg-black/20">
              <h3 className="text-[#F5F0E6] font-bold text-sm flex items-center gap-2">
                <ListFilter size={16} className="text-[#C9A962]" />
                {l('Libro de apuntes', 'Manual ledger')}
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {['all', 'income', 'expense'].map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilterType(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      filterType === key
                        ? 'bg-[#C9A962] text-[#0A1612]'
                        : 'bg-white/5 text-[#A1A1AA] hover:text-[#F5F0E6]'
                    }`}
                  >
                    {key === 'all' ? l('Todos', 'All') : key === 'income' ? l('Ingresos', 'Income') : l('Gastos', 'Expenses')}
                  </button>
                ))}
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="bg-[#0A0A0E] border border-white/10 text-[#F5F0E6] rounded-lg px-2 py-1.5 text-xs outline-none"
                >
                  <option value="date_desc">{l('Fecha ↓', 'Date ↓')}</option>
                  <option value="date_asc">{l('Fecha ↑', 'Date ↑')}</option>
                  <option value="amount_desc">{l('Importe ↓', 'Amount ↓')}</option>
                  <option value="amount_asc">{l('Importe ↑', 'Amount ↑')}</option>
                </select>
              </div>
            </div>

            {filteredSortedEntries.length === 0 ? (
              <p className="text-[#52525B] text-center py-12 text-sm px-4">
                {l('No hay entradas que coincidan. Añade gastos o ingresos con el botón superior.', 'No matching entries. Add expenses or income using the button above.')}
              </p>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[#52525B] text-xs uppercase border-b border-white/10">
                        <th className="px-4 py-2.5 font-semibold w-28">{l('Fecha', 'Date')}</th>
                        <th className="px-4 py-2.5 font-semibold w-24">{l('Tipo', 'Type')}</th>
                        <th className="px-4 py-2.5 font-semibold">{l('Categoría', 'Category')}</th>
                        <th className="px-4 py-2.5 font-semibold">{l('Descripción', 'Description')}</th>
                        <th className="px-4 py-2.5 font-semibold text-right w-28">{l('Importe', 'Amount')}</th>
                        <th className="px-4 py-2.5 w-12" aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSortedEntries.map((entry) => (
                        <tr key={entry.entry_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="px-4 py-3 text-[#A1A1AA] text-xs whitespace-nowrap tabular-nums">
                            {(entry.date || '').split('T')[0]}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${
                                entry.type === 'income'
                                  ? 'bg-[#10B981]/15 text-[#34D399]'
                                  : 'bg-[#EF4444]/15 text-[#F87171]'
                              }`}
                            >
                              {entry.type === 'income' ? (
                                <ArrowUpNarrowWide size={12} />
                              ) : (
                                <ArrowDownWideNarrow size={12} />
                              )}
                              {entry.type === 'income' ? l('In', 'In') : l('Out', 'Out')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#C9A962] text-xs">{getCatLabel(entry.type, entry.category)}</td>
                          <td className="px-4 py-3 text-[#F5F0E6] max-w-[240px] truncate">
                            {entry.description || '—'}
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-bold tabular-nums ${
                              entry.type === 'income' ? 'text-[#34D399]' : 'text-[#F87171]'
                            }`}
                          >
                            {entry.type === 'income' ? '+' : '-'}
                            {entry.amount?.toFixed(2)}€
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => handleDelete(entry.entry_id)}
                              className="p-2 rounded-lg text-[#EF4444] hover:bg-red-500/10"
                              title={l('Eliminar', 'Delete')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-white/5">
                  {filteredSortedEntries.map((entry) => (
                    <div key={entry.entry_id} className="px-4 py-3 flex items-start gap-3">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          entry.type === 'income' ? 'bg-[#10B981]/20' : 'bg-[#EF4444]/20'
                        }`}
                      >
                        {entry.type === 'income' ? (
                          <TrendingUp size={15} className="text-[#34D399]" />
                        ) : (
                          <TrendingDown size={15} className="text-[#F87171]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#F5F0E6] text-sm font-medium truncate">
                          {entry.description || getCatLabel(entry.type, entry.category)}
                        </p>
                        <p className="text-[#52525B] text-xs">
                          {getCatLabel(entry.type, entry.category)} · {(entry.date || '').split('T')[0]}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`font-bold text-sm tabular-nums ${
                            entry.type === 'income' ? 'text-[#34D399]' : 'text-[#F87171]'
                          }`}
                        >
                          {entry.type === 'income' ? '+' : '-'}
                          {entry.amount?.toFixed(2)}€
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry.entry_id)}
                          className="p-1 rounded text-[#EF4444]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {showInfo && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-2xl bg-[#12121A] border border-[#C9A962]/25 rounded-2xl shadow-2xl p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-[#F5F0E6] font-bold text-lg" style={{ fontFamily: 'Cinzel, serif' }}>
                  {l('Cómo funciona facturación y Stripe', 'How billing and Stripe work')}
                </h3>
                <p className="text-[#A1A1AA] text-sm mt-1">
                  {l(
                    'Resumen general de las funciones del panel en esta sección.',
                    'General overview of panel features in this section.',
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className="text-[#A1A1AA] hover:text-[#F5F0E6] p-1"
                aria-label={l('Cerrar', 'Close')}
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-4 space-y-3 text-sm">
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-[#C9A962] font-semibold">{l('1) Facturación interna', '1) Internal billing')}</p>
                <p className="text-[#D4D4D8] mt-1">
                  {l(
                    'Combina ventas de pedidos (Stripe) + apuntes manuales para calcular ingresos, gastos y neto por periodo.',
                    'Combines order sales (Stripe) + manual entries to calculate income, expenses, and net by period.',
                  )}
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-[#C9A962] font-semibold">{l('2) Conciliación Stripe', '2) Stripe reconciliation')}</p>
                <p className="text-[#D4D4D8] mt-1">
                  {l(
                    'Lista también transacciones locales en pendiente/iniciado sin pedido. «Revalidar live» consulta Stripe y marca cobros que aún no se reflejaron en BD. Incluye comprobación de discrepancias en reembolsos.',
                    'Also lists local transactions still pending/initiated with no order. “Live recheck” queries Stripe and flags charges not yet reflected in the DB. Includes refund mismatch checks.',
                  )}
                </p>
                <p className="text-[#A1A1AA] mt-2 text-xs">
                  {l(
                    'Tooltip: «Discrepancia live Stripe» solo muestra desajustes reales de estado entre local y Stripe. «Fallo verificación live Stripe» indica que la consulta a Stripe falló (red/permisos/clave) y no se pudo comparar.',
                    'Tooltip: “Live Stripe mismatch” only includes real local-vs-Stripe status mismatches. “Live Stripe check failed” means Stripe query failed (network/permissions/key), so comparison could not be made.',
                  )}
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-[#C9A962] font-semibold">{l('3) Reembolsos', '3) Refunds')}</p>
                <p className="text-[#D4D4D8] mt-1">
                  {l(
                    'Cuando un pedido se marca como "refunded" desde el panel, se ejecuta el reembolso real en Stripe y se sincroniza en base de datos.',
                    'When an order is marked as "refunded" in the panel, a real Stripe refund is executed and synced to the database.',
                  )}
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <p className="text-[#C9A962] font-semibold">{l('4) Buenas prácticas', '4) Best practices')}</p>
                <p className="text-[#D4D4D8] mt-1">
                  {l(
                    'Usa este panel para operación diaria y conciliación rápida; para auditoría financiera final, valida también en Stripe Dashboard.',
                    'Use this panel for day-to-day operations and quick reconciliation; for final financial audits, also verify in Stripe Dashboard.',
                  )}
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className="btn-primary px-4 py-2 text-sm"
              >
                {l('Entendido', 'Got it')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
